import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useTranslation } from "react-i18next";
import { Eye, Loader2, Timer, Check, X, Sparkles, RotateCcw } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { CinematicVideo } from "@/components/CinematicVideo";

const API = process.env.REACT_APP_BACKEND_URL;
const VIEW_SECONDS = 30;
const TEMOIN_VIDEO =
  "https://customer-assets-7cd3h4nn.emergentagent.net/job_epiphany-mentor/artifacts/zx2n23xu_gemini_generated_video_0b4415b5.mp4";

export const Temoin = () => {
  const { t } = useTranslation();
  const [phase, setPhase] = useState("idle"); // idle | loading | viewing | quiz | result
  const [challenge, setChallenge] = useState(null);
  const [seconds, setSeconds] = useState(VIEW_SECONDS);
  const [answers, setAnswers] = useState({});
  const [result, setResult] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const timerRef = useRef(null);

  useEffect(() => () => clearInterval(timerRef.current), []);

  const start = async () => {
    setPhase("loading");
    try {
      const { data } = await api.get("/witness/daily");
      setChallenge(data);
      setAnswers({});
      setResult(null);
      setSeconds(data.seconds || VIEW_SECONDS);
      setPhase("viewing");
    } catch (e) {
      toastLike(formatApiErrorDetail(e.response?.data?.detail));
      setPhase("idle");
    }
  };

  const toastLike = (msg) => { if (msg) import("sonner").then(({ toast }) => toast.error(msg)); };

  useEffect(() => {
    if (phase !== "viewing") return;
    timerRef.current = setInterval(() => {
      setSeconds((s) => {
        if (s <= 1) { clearInterval(timerRef.current); setPhase("quiz"); return 0; }
        return s - 1;
      });
    }, 1000);
    return () => clearInterval(timerRef.current);
  }, [phase]);

  const submit = async () => {
    if (submitting) return;
    setSubmitting(true);
    try {
      const { data } = await api.post("/witness/answer", { answers });
      setResult(data);
      setPhase("result");
    } catch (e) {
      toastLike(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setSubmitting(false); }
  };

  const restart = () => { setPhase("idle"); setChallenge(null); setResult(null); setAnswers({}); };

  const allAnswered = challenge && Object.keys(answers).length >= challenge.questions.length;
  const resultMsg = result
    ? (result.score === result.total ? t("temoin.perfect")
      : result.score >= Math.ceil(result.total / 2) ? t("temoin.good") : t("temoin.tryAgain"))
    : "";

  return (
    <section className="relative px-6 my-20" data-testid="temoin">
      <div className="max-w-4xl mx-auto">
        <div className="relative rounded-[2rem] overflow-hidden border border-primary/20 frame-pulse temoin-shell">
          <div className="relative z-10 px-6 sm:px-12 py-14">
            <div className="max-w-2xl mx-auto mb-8">
              <CinematicVideo
                silver
                src={TEMOIN_VIDEO}
                testId="temoin-cinematic-video"
                poster="/temoin-poster.jpg"
                caption={t("temoin.subtitle")}
                fade
                noSound
              />
            </div>
            <div className="text-center mb-8">
              <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-primary/15 border border-primary/40 text-primary text-[11px] uppercase tracking-[0.28em] mb-5" data-testid="temoin-badge">
                <Eye size={13} /> {t("temoin.badge")}
              </span>
              <h2 className="font-display text-gradient-orange text-4xl sm:text-6xl leading-[1.12] pb-[0.08em] mb-3" data-testid="temoin-title">
                {t("temoin.title")}
              </h2>
              <p className="text-foreground/65 text-base sm:text-lg max-w-xl mx-auto leading-relaxed">{t("temoin.subtitle")}</p>
            </div>

            <AnimatePresence mode="wait">
              {phase === "idle" && (
                <motion.div key="idle" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} className="text-center">
                  <button onClick={start} className="btn-frost-orange inline-flex items-center gap-3 px-8 py-4 rounded-full font-semibold text-base" data-testid="temoin-start-btn">
                    <Eye size={20} /> {t("temoin.start")}
                  </button>
                </motion.div>
              )}

              {phase === "loading" && (
                <motion.div key="loading" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="flex flex-col items-center py-8">
                  <Loader2 size={30} className="animate-spin text-primary mb-4" />
                  <p className="text-primary/70 text-sm uppercase tracking-[0.2em]">{t("temoin.loading")}</p>
                </motion.div>
              )}

              {phase === "viewing" && challenge && (
                <motion.div key="viewing" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
                  <div className="relative rounded-2xl overflow-hidden border border-white/10 mb-5" data-testid="temoin-image-wrap">
                    <img src={`${API}${challenge.image_url}`} alt="scène à mémoriser" className="w-full aspect-video object-cover" data-testid="temoin-image" />
                    <div className="absolute top-3 right-3 inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-black/60 backdrop-blur text-primary font-semibold text-sm" data-testid="temoin-countdown">
                      <Timer size={15} /> {seconds}s
                    </div>
                  </div>
                  <div className="h-1.5 rounded-full bg-white/10 overflow-hidden mb-5">
                    <div className="h-full bg-primary transition-all duration-1000 ease-linear" style={{ width: `${(seconds / (challenge.seconds || VIEW_SECONDS)) * 100}%` }} />
                  </div>
                  <div className="text-center">
                    <p className="text-foreground/55 text-sm italic mb-4">{t("temoin.watching")}</p>
                    <button onClick={() => { clearInterval(timerRef.current); setPhase("quiz"); }} className="inline-flex items-center gap-2 px-6 py-3 rounded-full border border-primary/30 text-primary hover:bg-primary/10 transition-colors font-semibold text-sm" data-testid="temoin-memorized-btn">
                      {t("temoin.memorized")}
                    </button>
                  </div>
                </motion.div>
              )}

              {phase === "quiz" && challenge && (
                <motion.div key="quiz" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} className="space-y-6">
                  {challenge.questions.map((q, qi) => (
                    <div key={q.id} data-testid={`temoin-question-${qi}`}>
                      <p className="font-display text-lg text-foreground/90 mb-3">{qi + 1}. {q.question}</p>
                      <div className="grid sm:grid-cols-2 gap-2.5">
                        {q.options.map((opt, oi) => (
                          <button key={oi} onClick={() => setAnswers((a) => ({ ...a, [q.id]: oi }))}
                            data-testid={`temoin-q${qi}-opt${oi}`}
                            className={`text-left px-4 py-3 rounded-xl border text-sm transition-colors ${answers[q.id] === oi ? "border-primary bg-primary/15 text-foreground" : "border-white/10 text-foreground/70 hover:border-primary/40"}`}>
                            {opt}
                          </button>
                        ))}
                      </div>
                    </div>
                  ))}
                  <div className="text-center pt-2">
                    <button onClick={submit} disabled={!allAnswered || submitting} className="btn-frost-orange inline-flex items-center gap-2 px-8 py-3.5 rounded-full font-semibold disabled:opacity-40" data-testid="temoin-submit-btn">
                      {submitting ? <Loader2 size={17} className="animate-spin" /> : <Check size={17} />} {t("temoin.submit")}
                    </button>
                    {!allAnswered && <p className="text-foreground/40 text-xs mt-3">{t("temoin.answerAll")}</p>}
                  </div>
                </motion.div>
              )}

              {phase === "result" && result && (
                <motion.div key="result" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} data-testid="temoin-result">
                  <div className="text-center mb-8">
                    <p className="font-display text-gradient-orange text-5xl mb-2" data-testid="temoin-score">{result.score}/{result.total}</p>
                    <p className="text-foreground/70">{resultMsg}</p>
                  </div>
                  <div className="space-y-3">
                    {result.corrections.map((c, ci) => {
                      const ok = c.given_index === c.correct_index;
                      return (
                        <div key={c.id} className="glass rounded-xl p-4 border border-white/8" data-testid={`temoin-correction-${ci}`}>
                          <div className="flex items-start gap-2.5">
                            <span className={`flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center ${ok ? "bg-green-500/20 text-green-400" : "bg-red-500/20 text-red-400"}`}>
                              {ok ? <Check size={14} /> : <X size={14} />}
                            </span>
                            <div>
                              <p className="text-foreground/85 text-sm mb-1">{c.question}</p>
                              <p className="text-primary/80 text-sm"><Sparkles size={12} className="inline mr-1" />{c.options[c.correct_index]}</p>
                              {!ok && c.given_index != null && (
                                <p className="text-foreground/40 text-xs mt-0.5 line-through">{c.options[c.given_index]}</p>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  <div className="text-center mt-8">
                    <button onClick={restart} className="inline-flex items-center gap-2 px-6 py-3 rounded-full border border-primary/30 text-primary hover:bg-primary/10 transition-colors font-semibold text-sm" data-testid="temoin-restart-btn">
                      <RotateCcw size={16} /> {t("temoin.retry")}
                    </button>
                    <p className="text-foreground/40 text-xs mt-4">{t("temoin.comeBack")}</p>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>
      </div>
    </section>
  );
};
